import React from 'react';

interface Props {
  // A label (e.g. the sender/customer) shown in the fallback so the row is still
  // identifiable when its card fails to render.
  label?: string;
  children: React.ReactNode;
}
interface State { hasError: boolean; message: string; }

// Isolates a single conversation card: if one card throws during render, only THAT
// card shows a small fallback — the rest of the feed keeps rendering. Without this,
// one bad card blanks the entire feed (React unmounts the whole tree on an uncaught
// render error). Error boundaries MUST be class components.
class CardErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, message: '' };
  }

  static getDerivedStateFromError(error: any): State {
    return { hasError: true, message: (error && error.message) || 'Render error' };
  }

  componentDidCatch(error: any, info: any) {
    // Surface for debugging without breaking the feed.
    // eslint-disable-next-line no-console
    console.error('ConversationCard render error (isolated):', error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="bg-white rounded-lg border border-red-200 shadow-sm px-4 py-3 text-sm text-red-700">
          <div className="font-semibold">
            Couldn't display this conversation{this.props.label ? ` (${this.props.label})` : ''}.
          </div>
          <div className="text-xs text-red-500 mt-0.5">
            The rest of your messages are unaffected. {this.state.message}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default CardErrorBoundary;
